import { func } from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components";

import { useMemberToobarContext } from "CrossFeatureComponents/MemberToolbar/MemberToolbarState";
import { useMemberToolbarStore } from "CrossFeatureComponents/MemberToolbar/memberToolbarStore";
import { INNER_MENU_BORDER_RADIUS } from "CrossFeatureComponents/MemberToolbar/styleConstants";

import FnrInput3 from "UI/FnrInput3";
import MultiLineInput from "UI/FnrInput3/Inputs/MultiLineInput";
import { marketingColors } from "UI/colors";

import { useIsTouchDevice } from "Utilities/Hooks/useIsTouchDevice";
import getCaretY from "Utilities/elementFunctions/getCaretY";

const Input = (props) => {
  const [oldScrollY, setOldScrollY] = useState();

  const { activeContentKey } = useMemberToolbarStore();
  const { message, setMessage, backButtonRef } = useMemberToobarContext();
  const inputRef = useRef();
  const refocusTimeout = useRef();
  const formActive = useMemo(
    () => activeContentKey === "form",
    [activeContentKey]
  );
  const isTouchDevice = useIsTouchDevice();

  useEffect(() => {
    if (!isTouchDevice) return; // This is for handling the keyboard popping out.
    if (formActive) {
      if (typeof inputRef?.current?.focus === "function" && formActive) {
        setOldScrollY(window.scrollY);
        inputRef.current.focus();
        setTimeout(() => {
          if (typeof window.scrollTo === "function") {
            window.scrollTo({
              top: backButtonRef.current.getBoundingClientRect().top,
              behavior: "smooth",
            });
          }
        }, 300);
      }
    } else {
      setTimeout(() => {
        window.scrollTo({
          top: oldScrollY,
          behavior: "smooth",
        });
      });
    }
  }, [formActive, backButtonRef, oldScrollY, isTouchDevice]);

  const onCaretMove = useCallback((e) => {
    const { target: elem } = e || {};
    if (!elem) return;
    if (typeof elem.scrollTo === "function") {
      const caretY = getCaretY(elem);
      const relativeCaretY = caretY - elem.scrollTop;
      const shouldScroll = elem.clientHeight - relativeCaretY < 120;

      if (shouldScroll)
        requestAnimationFrame(() => {
          elem.scrollTo({
            top: caretY - 120,
            behavior: "smooth", // This make manifest a voice to text bug, making the caret appear in the wrong spot...
          });
          clearTimeout(refocusTimeout.current);
          refocusTimeout.current = setTimeout(() => {
            elem.scrollTop = caretY - 120; // ...and this fixes it
          }, 500);
        });
    }
  }, []);

  const handleSelectionChange = useCallback(
    (e) => {
      const active = document.activeElement;
      if (active === inputRef.current) {
        onCaretMove(e);
      }
    },
    [onCaretMove]
  );

  useEffect(() => {
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [handleSelectionChange]);

  return (
    <SFnrInput3
      Component={SMultiLineInput}
      value={message}
      setValue={setMessage}
      data-cy="MemberToolbar_ContactInput"
      staticHeight
      autoFocus
      disabled={!formActive}
      ref={inputRef}
      charLimit={5000}
      placeholder="Type your message here..."
      {...props}
    />
  );
};

Input.propTypes = { onChange: func };

const SMultiLineInput = styled(MultiLineInput)`
  flex-grow: 1;
  padding-bottom: 60px;
`;

const SFnrInput3 = styled(FnrInput3)`
  --fnr-input-bg: ${marketingColors.grey};
  height: 230px;
  display: flex;
  flex-grow: 1;
  border-radius: ${INNER_MENU_BORDER_RADIUS}px;
  overflow: clip;
`;

export default Input;
